import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, get, patch } from "../../lib/api";
import { saleState, tariffLabel } from "../../lib/tariff";
import type { ChargePoint, Tariff } from "../../lib/types";
import { Button } from "../ui/Button";
import { Notice } from "../ui/Feedback";
import { SelectField } from "../ui/Input";
import { SettingRow } from "../ui/SettingRow";

/**
 * Putting a station on sale to the public. pay-ui spec §4.2, §6.1, §13.1.1.
 *
 * ## What this replaces
 *
 * An `UPDATE charge_point SET public_access = true` against the live database. That
 * is not an exaggeration — it is how every station in this deployment was put on
 * sale before this panel existed, and it is a commercial decision taken with no
 * audit trail, no validation, and no way for a second person to see it.
 *
 * ## The price is the decision, not the switch
 *
 * `charge_point_public_needs_tariff` means the two fields cannot be set
 * independently: a station on sale must have a price. So the form is one form. The
 * switch and the price are saved together, and *Take off sale* deliberately leaves
 * the price behind — putting a station back after a fault is then one press, and
 * re-choosing the price is the step where somebody picks the wrong one.
 *
 * ## Why the errors are the server's own words
 *
 * The invariant is checked here, in the route, and by Postgres. When the server
 * refuses, its sentence is shown verbatim rather than replaced with a generic
 * failure: it names the tariff, or says the price is retired, and that is the
 * operator's next action. A message this component invents would be one more place
 * for the three checks to disagree.
 *
 * ## Without the buttons
 *
 * An operator without the Change public sale permission gets the same row with no
 * actions — not the buttons disabled: a control that cannot be used is a question
 * about permissions, and the row is an answer about the station. A hidden station
 * gets no buttons either, because it cannot go on sale until it is unhidden.
 */
export function PublicAccessPanel({
  station,
  canEdit,
}: {
  readonly station: ChargePoint;
  readonly canEdit: boolean;
}) {
  const client = useQueryClient();
  const state = saleState(station);

  /**
   * Only when the operator is actually choosing. A station that is already on sale
   * shows its price as a sentence; the list is a request, and making it on mount
   * would fetch a price list on every station page whether or not anyone is pricing.
   */
  const [isEditing, setIsEditing] = useState(false);
  const [chosen, setChosen] = useState<string>(station.tariff?.id ?? "");
  const [failed, setFailed] = useState<string | undefined>();

  const tariffs = useQuery({
    queryKey: ["tariffs"],
    queryFn: () => get<readonly Tariff[]>("/tariffs"),
    enabled: isEditing,
  });

  const save = useMutation({
    mutationFn: (body: { publicAccess?: boolean; tariffId?: string }) =>
      patch<ChargePoint>(`/charge-points/${encodeURIComponent(station.id)}`, body),
    onSuccess: () => {
      setIsEditing(false);
      setFailed(undefined);
      // Both views read this station: the detail page's own query and the fleet
      // table behind it, which now has a Public column that would otherwise lie.
      void client.invalidateQueries({ queryKey: ["charge-point", station.id] });
      void client.invalidateQueries({ queryKey: ["charge-points"] });
      /**
       * And the price list, which is not about this station at all.
       *
       * Putting a station on a price — or taking it off one — is the only thing that
       * moves a tariff's `subscriberCount`, and that number is what the change form
       * warns with. Left stale, the warning names fewer stations than the button is
       * about to reprice, which is the one number on that screen nobody can check.
       */
      void client.invalidateQueries({ queryKey: ["tariffs"] });
    },
    onError: (error: unknown) => {
      setFailed(
        error instanceof ApiError ? error.message : "The change could not be saved. Try again.",
      );
    },
  });

  /** Active prices only, plus whatever this station already sells on. */
  const options = (tariffs.data ?? []).filter((row) => row.active || row.id === station.tariff?.id);

  const isHidden = station.hiddenAt !== null;
  const value =
    state.kind === "private"
      ? "Not on sale"
      : state.kind === "off_sale"
        ? `Off sale · last sold at ${state.price}`
        : `On sale at ${state.price}`;
  const hint = isHidden
    ? "A hidden station cannot go on sale; unhide it first."
    : state.kind === "private"
      ? "A driver scanning its sticker is told the charger cannot be used."
      : state.kind === "off_sale"
        ? "Can be put back without choosing a price again."
        : "A driver scanning the sticker is quoted this price.";

  return (
    <SettingRow
      label="Public charging"
      value={value}
      hint={hint}
      actions={
        !canEdit || isHidden || isEditing ? null : station.publicAccess ? (
          <>
            <Button onClick={() => setIsEditing(true)}>Change price</Button>
            <Button
              variant="ghost"
              disabled={save.isPending}
              onClick={() => {
                setFailed(undefined);
                // No `tariffId`: absent means unchanged, which is what keeps the
                // price for the next time this station goes back on sale.
                save.mutate({ publicAccess: false });
              }}
            >
              {save.isPending ? "Saving…" : "Take off sale"}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={() => setIsEditing(true)}>
            {state.kind === "off_sale" ? "Put back on sale" : "Put on sale"}
          </Button>
        )
      }
    >
      {state.kind === "on_sale_retired" ? (
        <Notice tone="error">
          This station is selling at a price that has been retired. Choose an active price, or take
          it off sale.
        </Notice>
      ) : null}

      {failed === undefined ? null : <Notice tone="error">{failed}</Notice>}

      {isEditing ? (
        <>
          <SelectField
            label="Price"
            value={chosen}
            hint="What a driver pays per kWh. Retired prices are not offered."
            disabled={tariffs.isPending || save.isPending}
            onChange={(event) => setChosen(event.target.value)}
          >
            <option value="">Choose a price…</option>
            {options.map((row) => (
              <option key={row.id} value={row.id}>
                {tariffLabel(row)}
              </option>
            ))}
          </SelectField>

          {tariffs.isError ? (
            <Notice tone="error">The price list could not be loaded.</Notice>
          ) : null}

          {/*
            No price to choose is a dead end worth naming rather than an empty
            dropdown — and now it is a dead end with a way out, so the notice
            carries the link rather than telling the operator who to ask.
          */}
          {!tariffs.isPending && options.length === 0 ? (
            <Notice tone="error">
              No active price exists to sell at.{" "}
              <Link to="/prices" className="underline decoration-1 underline-offset-4">
                Create one
              </Link>{" "}
              before putting a station on sale.
            </Notice>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              disabled={chosen === "" || save.isPending}
              onClick={() => {
                setFailed(undefined);
                save.mutate({ publicAccess: true, tariffId: chosen });
              }}
            >
              {save.isPending ? "Saving…" : "Put on sale"}
            </Button>
            <Button
              onClick={() => {
                setIsEditing(false);
                setFailed(undefined);
                setChosen(station.tariff?.id ?? "");
              }}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : null}
    </SettingRow>
  );
}
